import * as Sentry from '@sentry/nuxt'

export default defineNuxtPlugin((nuxtApp) => {
  const { $config } = useNuxtApp()

  // Get current environment
  const currentEnvironment = $config.public.appENV || 'development'
  const isProduction = currentEnvironment === 'production'

  // Check if Sentry DSN is configured
  if (!$config.public.sentryDsn || !isProduction) {
    return {
      provide: {
        sentry: null,
      },
    }
  }

  // Create release version that matches source map uploads
  const releaseVersion = `${
    process.env.npm_package_version || '2.0.0-sharesquare-next'
  }-${currentEnvironment}`

  Sentry.init({
    dsn: $config.public.sentryDsn,
    environment: currentEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance monitoring - higher sampling for non-production
    tracesSampleRate: currentEnvironment === 'production' ? 0.1 : 1.0,
    // Session replay - higher sampling for non-production
    replaysSessionSampleRate: currentEnvironment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking for source maps
    release: releaseVersion,
    beforeSend(event, hint) {
      try {
        // Add user context if available
        const user = useState('auth.user')
        if (user.value) {
          event.user = {
            id: user.value.id,
            email: user.value.email,
          }
        }
      } catch (e) {
        console.warn('Could not set Sentry user context:', e)
      }

      // Add tags for better filtering
      event.tags = {
        ...event.tags,
        component: 'frontend',
        framework: 'nuxt3',
        environment: currentEnvironment,
      }

      return event
    },
    captureUnhandledRejections: true,
  })

  // Set up Vue error handler
  const vueApp = nuxtApp.vueApp
  vueApp.config.errorHandler = (error, instance, info) => {
    console.error('Vue Error Handler:', error, info)
    Sentry.captureException(error, {
      contexts: {
        vue: {
          componentName:
            instance?.$options.name || instance?.$options.__name || 'Unknown',
          propsData: instance?.$props,
          lifecycle: info,
        },
      },
    })
  }

  // Global error handlers for client-side
  if (process.client) {
    // Global JavaScript error handler
    window.addEventListener('error', (event) => {
      console.error('Global Error Handler:', event.error)
      Sentry.captureException(event.error || new Error(event.message), {
        contexts: {
          javascript: {
            message: event.message,
            source: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        },
      })
    })

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason)
      Sentry.captureException(event.reason, {
        contexts: {
          promise: {
            reason: event.reason,
          },
        },
      })
    })
  }

  // Nuxt app error handler
  nuxtApp.hook('app:error', (error) => {
    console.error('Nuxt App Error:', error)
    Sentry.captureException(error, {
      tags: {
        errorBoundary: 'nuxt',
      },
    })
  })

  return {
    provide: {
      sentry: Sentry,
    },
  }
})
